// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useId, useState } from 'react';
import { Button } from '@carbon/react';
import styles from './ProcedureFormFooter.module.css';

type Bump = 'patch' | 'minor';

interface ProcedureFormFooterProps {
  version: string;
  onCancel: () => void;
  onSubmit: (nextVersion: string) => void;
  isSaving?: boolean;
  /** False when the guided form has an unmet required field — see
   *  `validateProcedure()` in `shared/procedures`. */
  canSubmit?: boolean;
  error?: string | null;
  /** Set when the backend can't accept a submission at all right now (not a
   *  form-validity problem) — forces Submit disabled and shows this instead
   *  of waiting for a failed attempt to explain why. */
  disabledReason?: string | null;
}

/** Raw (no "v" prefix) next-version strings — that's the form the field
 *  itself uses; "v" is only added for the button's own display label. */
function nextVersions(version: string): Record<Bump, string> {
  const [rawMajor, rawMinor] = version.split('.');
  const major = Number.parseInt(rawMajor, 10) || 0;
  const minor = Number.parseInt(rawMinor, 10) || 0;
  return {
    patch: `${major}.${minor + 1}`,
    minor: `${major + 1}.0`,
  };
}

/**
 * Fixed footer for the guided-form pane. Version bump choice is local
 * component state, but the chosen next version is passed up via
 * `onSubmit(nextVersion)` so the caller can actually save it — Cancel is
 * still a plain no-argument callback since it just discards and navigates.
 */
export default function ProcedureFormFooter({
  version,
  onCancel,
  onSubmit,
  isSaving = false,
  canSubmit = true,
  error = null,
  disabledReason = null,
}: ProcedureFormFooterProps) {
  const [bump, setBump] = useState<Bump>('patch');
  const next = nextVersions(version);
  const message = disabledReason ?? error;
  const messageId = useId();

  return (
    <div className={styles.footer}>
      <div className={styles.bumpGroup}>
        <span className={styles.bumpLabel}>Version bump:</span>
        <button
          type="button"
          className={`${styles.bumpButton} ${bump === 'patch' ? styles.bumpButtonSelected : ''}`}
          onClick={() => setBump('patch')}
          disabled={isSaving || Boolean(disabledReason)}
        >
          Patch → v{next.patch}
        </button>
        <button
          type="button"
          className={`${styles.bumpButton} ${bump === 'minor' ? styles.bumpButtonSelected : ''}`}
          onClick={() => setBump('minor')}
          disabled={isSaving || Boolean(disabledReason)}
        >
          Minor → v{next.minor}
        </button>
      </div>
      {message && (
        <span id={messageId} className={disabledReason ? styles.notice : styles.error}>
          {message}
        </span>
      )}
      <div className={styles.actions}>
        <Button kind="secondary" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="sm"
          onClick={() => onSubmit(next[bump])}
          disabled={isSaving || !canSubmit || Boolean(disabledReason)}
          aria-describedby={message ? messageId : undefined}
        >
          {isSaving ? 'Submitting…' : 'Submit request'}
        </Button>
      </div>
    </div>
  );
}
